import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Zap, Crown, Loader2 } from "lucide-react";
import {
  TIER_PRICES_USD,
  TIER_GEOGRID_CREDITS,
  TIER_LOCATION_LIMITS,
  TIER_FEATURES,
  TRIAL_DAYS,
  TRIAL_INITIAL_PRICE_USD,
  type PlanTier,
} from "@shared/schema";

type SubTier = Exclude<PlanTier, "free">;

interface BillingConfig {
  whopEnabled: boolean;
  hasStarterPlan: boolean;
  hasGrowthPlan: boolean;
  hasDominatorPlan: boolean;
}

interface BillingStatus {
  status: string;
  trialEndsAt: string | null;
  hasMembership: boolean;
}

interface MeResponse {
  id: string;
  tier?: PlanTier | null;
}

const TIER_COPY: Record<SubTier, {
  name: string;
  tagline: string;
  icon: typeof Sparkles;
  highlights: string[];
  badge?: string;
}> = {
  starter: {
    name: "Local Domination",
    tagline: "Outrank every competitor in town. Full autopilot for 1 location.",
    icon: Sparkles,
    badge: "Most Popular Starter",
    highlights: [
      "Full autopilot for 1 Google Business Profile",
      "1,690 GeoGrid credits / 28 days (≈10 full 13×13 scans)",
      "AI-written GBP posts shipped every week",
      "Review request + auto-reply engine (4–5★ auto, ≤3★ approval gated)",
      "Aria — your AI growth assistant",
      "Listing protection & citation monitoring",
      "Monthly plain-English performance report",
    ],
  },
  growth: {
    name: "Multi-Location Domination",
    tagline: "Consistent leads everywhere — up to 3 locations from one dashboard.",
    icon: Zap,
    badge: "Best Value",
    highlights: [
      "Everything in Local Domination, plus:",
      "Manage up to 3 locations from one dashboard",
      "5,070 GeoGrid credits / 28 days (≈30 full scans)",
      "Multi-location reporting (see which store is winning)",
      "Shared review templates so every location sounds on-brand",
      "Product Forge for branded GBP product cards",
    ],
  },
  dominator: {
    name: "Market Monopoly",
    tagline: "Own your entire service area. Up to 5 locations, white-glove onboarding.",
    icon: Crown,
    highlights: [
      "Everything in Multi-Location Domination, plus:",
      "Up to 5 locations included",
      "16,900 GeoGrid credits / 28 days (≈100 full scans)",
      "Priority support with faster response times",
      "White-glove onboarding for every new location",
      "Need more than 5 locations? Add extras for $175 / 28 days each.",
    ],
  },
};

const TIER_ORDER: SubTier[] = ["starter", "growth", "dominator"];
const TIER_RANK: Record<PlanTier, number> = { free: 0, starter: 1, growth: 2, dominator: 3 };

export default function PricingPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [pendingTier, setPendingTier] = useState<SubTier | null>(null);

  const { data: me } = useQuery<MeResponse>({ queryKey: ["/api/user"] });
  const { data: billingConfig } = useQuery<BillingConfig>({ queryKey: ["/api/billing/config"] });
  const { data: billingStatus } = useQuery<BillingStatus>({ queryKey: ["/api/billing/status"] });

  const currentTier: PlanTier = (me?.tier as PlanTier) || "free";

  // When Stripe redirects back to /pricing?subscribed=<tier> after a completed
  // $1 trial checkout, strip the param so a refresh doesn't re-process it. The
  // conversion events themselves (Google Ads + Meta Purchase/CAPI) are fired by
  // the app-wide ConversionTracker, keyed off the Stripe session_id.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed")) {
      params.delete("subscribed");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const handleSubscribe = (tier: SubTier) => {
    setPendingTier(tier);
    setLocation(`/checkout?tier=${tier}`);
  };

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/billing/cancel", {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({ title: "Subscription canceled", description: "Your plan will end at the close of the current billing period." });
    },
    onError: (err: Error) => {
      toast({ title: "Cancellation failed", description: err.message, variant: "destructive" });
    },
  });

  const planConfigured = (tier: SubTier): boolean => {
    if (!billingConfig) return false;
    if (tier === "starter") return billingConfig.hasStarterPlan;
    if (tier === "growth") return billingConfig.hasGrowthPlan;
    return billingConfig.hasDominatorPlan;
  };

  const ctaLabel = (tier: SubTier): string => {
    if (currentTier === tier) return "Current Plan";
    const isUpgrade = TIER_RANK[tier] > TIER_RANK[currentTier];
    if (currentTier === "free") return `Start $${TRIAL_INITIAL_PRICE_USD} / ${TRIAL_DAYS}-day trial`;
    return isUpgrade ? "Upgrade" : "Switch";
  };

  return (
    <div className="h-full overflow-y-auto">
    <div className="container mx-auto py-10 px-4 max-w-7xl">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="text-pricing-title">
          Pick the plan that fits your reach
        </h1>
        <p className="text-muted-foreground mt-3 text-lg max-w-2xl mx-auto">
          Every plan includes the full Digital Domination platform — GeoGrid tracking, GBP optimization,
          listing protection, and citation tools. Tiers differ by locations covered, scan volume per 28-day cycle,
          and which growth automations are unlocked.
        </p>
        {currentTier !== "free" && (
          <Badge variant="secondary" className="mt-4" data-testid="badge-current-tier-banner">
            You're on the {TIER_COPY[currentTier as SubTier]?.name || currentTier} plan
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIER_ORDER.map((tier) => {
          const copy = TIER_COPY[tier];
          const Icon = copy.icon;
          const price = TIER_PRICES_USD[tier];
          const credits = TIER_GEOGRID_CREDITS[tier];
          const locations = TIER_LOCATION_LIMITS[tier];
          const isCurrent = currentTier === tier;
          const isHighlighted = tier === "growth";
          const configured = planConfigured(tier);
          const isPending = pendingTier === tier;

          return (
            <Card
              key={tier}
              className={`relative flex flex-col ${
                isHighlighted ? "border-2 border-brand-orange shadow-lg" : ""
              } ${isCurrent ? "ring-2 ring-brand-blue" : ""}`}
              data-testid={`card-tier-${tier}`}
            >
              {copy.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-brand-orange text-white hover:bg-brand-orange" data-testid={`badge-tier-${tier}-popular`}>
                    {copy.badge}
                  </Badge>
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 right-4">
                  <Badge variant="secondary" data-testid={`badge-tier-${tier}-current`}>Active</Badge>
                </div>
              )}

              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${isHighlighted ? "text-brand-orange" : "text-brand-blue"}`} />
                  <CardTitle data-testid={`text-tier-${tier}-name`}>{copy.name}</CardTitle>
                </div>
                <CardDescription>{copy.tagline}</CardDescription>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-bold" data-testid={`text-tier-${tier}-price`}>${price}</span>
                  <span className="text-muted-foreground">/ 28 days</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {locations} location{locations === 1 ? "" : "s"} • {credits.toLocaleString()} scan credits / 28 days
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <ul className="space-y-2 flex-1">
                  {copy.highlights.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-500 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 space-y-2">
                  {isCurrent ? (
                    <>
                      <Button
                        variant="secondary"
                        className="w-full"
                        disabled
                        data-testid={`button-tier-${tier}-current`}
                      >
                        Current Plan
                      </Button>
                      {billingStatus?.hasMembership && billingStatus.status !== "canceled" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-muted-foreground"
                          disabled={cancelMutation.isPending}
                          onClick={() => {
                            if (confirm("Cancel your subscription at the end of the current billing period?")) {
                              cancelMutation.mutate();
                            }
                          }}
                          data-testid={`button-tier-${tier}-cancel`}
                        >
                          {cancelMutation.isPending ? "Canceling…" : "Cancel subscription"}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        className={`w-full ${
                          isHighlighted
                            ? "bg-brand-orange hover:bg-brand-orange/90 text-white"
                            : ""
                        }`}
                        disabled={!configured || isPending}
                        onClick={() => handleSubscribe(tier)}
                        data-testid={`button-tier-${tier}-subscribe`}
                      >
                        {isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Redirecting…
                          </>
                        ) : (
                          ctaLabel(tier)
                        )}
                      </Button>
                      {!configured && (
                        <p className="text-xs text-muted-foreground text-center" data-testid={`text-tier-${tier}-not-configured`}>
                          Checkout coming soon
                        </p>
                      )}
                      {configured && currentTier === "free" && (
                        <p className="text-xs text-muted-foreground text-center" data-testid={`text-tier-${tier}-trial-note`}>
                          ${TRIAL_INITIAL_PRICE_USD} for {TRIAL_DAYS} days · Then ${TIER_PRICES_USD[tier]} every 28 days · Cancel anytime
                        </p>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p>
          Every plan auto-renews every 28 days. Cancel anytime — your access stays active through the end of your billing period.
        </p>
        <p className="mt-2">
          Need more than 5 locations or want to discuss enterprise terms?{" "}
          <button
            type="button"
            className="underline text-brand-blue hover:text-brand-blue/80"
            onClick={() => setLocation("/settings")}
            data-testid="link-enterprise-contact"
          >
            Reach out
          </button>{" "}
          and your account manager will follow up.
        </p>
      </div>

      <FeatureMatrix currentTier={currentTier} />
    </div>
    </div>
  );
}

function FeatureMatrix({ currentTier }: { currentTier: PlanTier }) {
  const rows: Array<{ key: keyof typeof TIER_FEATURES["growth"]; label: string }> = [
    { key: "autoReplyReviews", label: "Auto-reply to Google reviews" },
    { key: "autoSchedulePosts", label: "Auto-publish GBP posts" },
    { key: "weeklyReports", label: "Weekly performance reports (emailed)" },
    { key: "aria", label: "Aria AI assistant" },
  ];

  return (
    <div className="mt-12 max-w-4xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-center">What's unlocked at each tier</h2>
      <div className="border rounded-md overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium">Feature</th>
              {TIER_ORDER.map((t) => (
                <th key={t} className="p-3 font-medium capitalize" data-testid={`th-matrix-${t}`}>
                  {t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t">
              <td className="p-3 font-medium">Locations included</td>
              {TIER_ORDER.map((t) => (
                <td key={t} className={`p-3 text-center ${currentTier === t ? "bg-brand-blue/5" : ""}`}>
                  {TIER_LOCATION_LIMITS[t]}
                </td>
              ))}
            </tr>
            <tr className="border-t">
              <td className="p-3 font-medium">Monthly scan credits</td>
              {TIER_ORDER.map((t) => (
                <td key={t} className={`p-3 text-center ${currentTier === t ? "bg-brand-blue/5" : ""}`}>
                  {TIER_GEOGRID_CREDITS[t].toLocaleString()}
                </td>
              ))}
            </tr>
            {rows.map((row) => (
              <tr key={row.key} className="border-t">
                <td className="p-3 font-medium">{row.label}</td>
                {TIER_ORDER.map((t) => (
                  <td key={t} className={`p-3 text-center ${currentTier === t ? "bg-brand-blue/5" : ""}`}>
                    {TIER_FEATURES[t][row.key] ? (
                      <Check className="w-4 h-4 text-green-600 dark:text-green-500 mx-auto" />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
